/**
 * "Groups nothing has set": the honest stand-in for FR-CFM-012's go-live
 * checklist (ORDR-119, item 7).
 *
 * IT IS NOT THE CHECKLIST, AND SAYS SO. Ordering's plane has no `required_at`
 * and no registry, so nothing here can know what a location must set before
 * it trades. What it CAN say is which fetched groups nothing in the ancestry
 * sets. A module not yet opened contributes a prompt to open it, never a false
 * "nothing unset".
 */

import type { ReactElement } from "react";
import { moduleDomains, type CadenceModule } from "./cadence.js";
import type { Groups } from "./useGroups.js";

export interface UnsetGroupsProps {
  readonly modules: readonly CadenceModule[];
  readonly opened: ReadonlySet<string>;
  readonly groups: Groups;
  readonly nodeName: string;
}

export function UnsetGroups({ modules, opened, groups, nodeName }: UnsetGroupsProps): ReactElement {
  return (
    <section data-testid="unset-groups" className="text-sm">
      <h3 className="font-medium">Groups nothing has set</h3>
      <p>
        This is not a go-live checklist. Ordering&apos;s settings do not record which groups must be set before a
        location takes orders, so nothing here can say what is required. These groups are simply unset for{" "}
        {nodeName} and every node above it; built-in defaults apply to them.
      </p>
      <ul className="list-disc pl-5">
        {modules.map((module) => {
          if (!opened.has(module.key)) {
            return <li key={module.key}>Open {module.title} to check its groups.</li>;
          }
          const unset = [...new Set(moduleDomains(module))].filter((domain) => {
            const state = groups.states[domain];
            return state?.status === "ready" && state.reading.sourceNodeId === null;
          });
          return unset.map((domain) => (
            <li key={`${module.key}-${domain}`} data-testid={`unset-${domain}`}>
              {domain} ({module.title})
            </li>
          ));
        })}
      </ul>
    </section>
  );
}
